import type { ViewerSettings } from '../types/settings.js';

type LanguageCode = ViewerSettings['language'];

export type TutorialInteractionType = 'pinch' | 'two-finger-drag' | 'tap' | 'rotate' | 'custom';
export type TutorialAnimationPreset = 'pinch-zoom' | 'pan-object' | 'tap-highlight' | 'rotate-object' | 'custom';

export interface TutorialInteractionStep {
  id: string;
  title: string;
  description: string;
}

export interface TutorialInteractionDetail {
  id: string;
  icon: string;
  title: string;
  shortDescription: string;
  detailedDescription: string;
  interactionType: TutorialInteractionType;
  deviceType?: 'touch' | 'mouse' | 'keyboard';
  animationPreset: TutorialAnimationPreset;
  steps: TutorialInteractionStep[];
  practicePrompt?: string;
  successMessage?: string;
  tips?: string[];
  warnings?: string[];
  hintLabel?: string;
}

const BASE_TUTORIAL_INTERACTIONS: TutorialInteractionDetail[] = [
  {
    id: 'pinch-to-zoom',
    icon: 'pinch',
    title: 'Pinch to Zoom',
    shortDescription: 'Pinch to zoom in or out of the model.',
    detailedDescription:
      'Use two fingers to smoothly change your distance from the model. Spreading your fingers apart zooms in for close inspection, while bringing them inward zooms out for a wider view.',
    interactionType: 'pinch',
    deviceType: 'touch',
    animationPreset: 'pinch-zoom',
    practicePrompt: 'Try pinching the model to zoom in or out.',
    successMessage: 'Nice! You changed the zoom level.',
    hintLabel: 'Move both fingers evenly for precise zoom control.',
    steps: [
      {
        id: 'pinch-1',
        title: 'Place two fingers down',
        description: 'Rest two fingers on the screen near the model without lifting the device.',
      },
      {
        id: 'pinch-2',
        title: 'Spread apart to zoom in',
        description: 'Move both fingers away from each other to get closer to the model.',
      },
      {
        id: 'pinch-3',
        title: 'Pinch inward to zoom out',
        description: 'Bring both fingers together to pull back and see more of the object.',
      },
      {
        id: 'pinch-4',
        title: 'Use smooth motion',
        description: 'Move slowly and evenly to avoid sudden jumps in zoom distance.',
      },
    ],
    tips: ['Keep both fingers moving at a similar speed.', 'Pause briefly after zooming before starting another gesture.'],
    warnings: ['Using one finger instead will usually rotate the model instead of zooming.'],
  },
  {
    id: 'two-finger-drag',
    icon: 'pan_tool',
    title: 'Two-Finger Drag',
    shortDescription: 'Drag with two fingers to move the object.',
    detailedDescription:
      'Use two fingers together to shift the model across the viewport. This is helpful when you want to re-center a feature without changing zoom or rotation too much.',
    interactionType: 'two-finger-drag',
    deviceType: 'touch',
    animationPreset: 'pan-object',
    practicePrompt: 'Try dragging with two fingers to reposition the model.',
    successMessage: 'Great! You repositioned the view.',
    hintLabel: 'Both fingers should travel together in the same direction.',
    steps: [
      {
        id: 'drag-1',
        title: 'Place two fingers on the screen',
        description: 'Touch the screen with two fingers at the same time to start a pan gesture.',
      },
      {
        id: 'drag-2',
        title: 'Move both fingers together',
        description: 'Slide both fingers in the same direction to reposition the model inside the frame.',
      },
      {
        id: 'drag-3',
        title: 'Watch the viewer shift',
        description: 'The model should translate with your fingers rather than rotate around its center.',
      },
      {
        id: 'drag-4',
        title: 'Release to settle',
        description: 'Lift both fingers to finish the movement once the model is framed correctly.',
      },
    ],
    tips: ['Use short motions for subtle re-centering.', 'Pan after zooming if a detail is too close to one edge of the screen.'],
    warnings: ['If your fingers move at different angles, the viewer may interpret another gesture.'],
  },
  {
    id: 'tap-for-info',
    icon: 'ads_click',
    title: 'Tap for Info',
    shortDescription: 'Tap a part of the model to view more information.',
    detailedDescription:
      'Tap directly on a visible part to request contextual information. The interface should highlight the selected object and reveal supporting details so you can understand what you are looking at.',
    interactionType: 'tap',
    deviceType: 'touch',
    animationPreset: 'tap-highlight',
    practicePrompt: 'Try tapping a visible component to inspect it.',
    successMessage: 'Perfect! You selected a part for inspection.',
    hintLabel: 'Aim for a clearly visible part to get the best result.',
    steps: [
      {
        id: 'tap-1',
        title: 'Find the target part',
        description: 'Look for a visible component you want to inspect more closely.',
      },
      {
        id: 'tap-2',
        title: 'Tap once',
        description: 'Use a single, clean tap instead of dragging or pressing repeatedly.',
      },
      {
        id: 'tap-3',
        title: 'Watch for highlight feedback',
        description: 'The selected part should glow or respond visually to confirm the selection.',
      },
      {
        id: 'tap-4',
        title: 'Read the details',
        description: 'An information panel or contextual label will appear with more detail about the part.',
      },
    ],
    tips: ['Rotate the model first if the part you need is hidden.', 'Tap once and wait a moment for the selection to resolve.'],
    warnings: ['Dragging instead of tapping may rotate the model and interrupt selection.'],
  },
  {
    id: 'one-finger-rotate',
    icon: '3d_rotation',
    title: 'One-Finger Rotate',
    shortDescription: 'Drag with one finger to rotate the model.',
    detailedDescription:
      'Use one finger to orbit around the model and reveal new surfaces. This is the primary way to inspect shape, connections, and hidden areas from multiple angles.',
    interactionType: 'rotate',
    deviceType: 'touch',
    animationPreset: 'rotate-object',
    practicePrompt: 'Try dragging with one finger to rotate the model.',
    successMessage: 'Nice! You rotated the model.',
    hintLabel: 'Longer drags create larger orbit changes.',
    steps: [
      {
        id: 'rotate-1',
        title: 'Touch with one finger',
        description: 'Place a single finger on the screen to begin the rotation gesture.',
      },
      {
        id: 'rotate-2',
        title: 'Drag left or right',
        description: 'Move your finger horizontally to orbit around the object and reveal different sides.',
      },
      {
        id: 'rotate-3',
        title: 'Watch the model pivot',
        description: 'The model should turn around its center rather than slide across the frame.',
      },
      {
        id: 'rotate-4',
        title: 'Fine-tune the angle',
        description: 'Use shorter drags for subtle viewpoint adjustments and precise inspection.',
      },
    ],
    tips: ['Rotate slowly when checking fine details.', 'Combine rotation with zoom to inspect hidden geometry.'],
    warnings: ['Using two fingers will likely pan instead of rotate.'],
  },
];

interface LocalizedTutorialStep {
  title: string;
  description: string;
}

interface LocalizedTutorialInteraction {
  title: string;
  shortDescription: string;
  detailedDescription: string;
  practicePrompt?: string;
  successMessage?: string;
  hintLabel?: string;
  tips?: string[];
  warnings?: string[];
  steps: LocalizedTutorialStep[];
}

const TUTORIAL_TRANSLATIONS: Partial<Record<LanguageCode, Record<string, LocalizedTutorialInteraction>>> = {
  es: {
    'pinch-to-zoom': {
      title: 'Pellizcar para hacer zoom',
      shortDescription: 'Pellizca para acercar o alejar el modelo.',
      detailedDescription: 'Usa dos dedos para cambiar suavemente tu distancia respecto al modelo. Separarlos acerca la vista para una inspecci\u00f3n detallada y juntarlos la aleja para una vista m\u00e1s amplia.',
      practicePrompt: 'Prueba a pellizcar el modelo para acercarlo o alejarlo.',
      successMessage: 'Bien. Has cambiado el nivel de zoom.',
      hintLabel: 'Mueve ambos dedos de forma uniforme para controlar el zoom con precisi\u00f3n.',
      tips: ['Mant\u00e9n ambos dedos movi\u00e9ndose a una velocidad similar.', 'Haz una breve pausa despu\u00e9s del zoom antes de iniciar otro gesto.'],
      warnings: ['Si usas un solo dedo, normalmente el modelo girar\u00e1 en lugar de hacer zoom.'],
      steps: [
        { title: 'Coloca dos dedos', description: 'Apoya dos dedos sobre la pantalla cerca del modelo sin levantar el dispositivo.' },
        { title: 'Sep\u00e1ralos para acercar', description: 'Mueve ambos dedos en direcciones opuestas para acercarte al modelo.' },
        { title: 'J\u00fantalos para alejar', description: 'Acerca ambos dedos para alejar la vista y ver m\u00e1s del objeto.' },
        { title: 'Usa un movimiento suave', description: 'Mu\u00e9vete despacio y de forma uniforme para evitar saltos bruscos en la distancia.' },
      ],
    },
    'two-finger-drag': {
      title: 'Arrastre con dos dedos',
      shortDescription: 'Arrastra con dos dedos para mover el objeto.',
      detailedDescription: 'Usa dos dedos al mismo tiempo para desplazar el modelo dentro de la vista. Esto ayuda a volver a centrar un detalle sin cambiar demasiado el zoom o la rotaci\u00f3n.',
      practicePrompt: 'Prueba a arrastrar con dos dedos para recolocar el modelo.',
      successMessage: 'Genial. Has reposicionado la vista.',
      hintLabel: 'Ambos dedos deben moverse juntos en la misma direcci\u00f3n.',
      tips: ['Usa movimientos cortos para recentrar con suavidad.', 'Haz el desplazamiento despu\u00e9s del zoom si un detalle queda demasiado cerca de un borde.'],
      warnings: ['Si tus dedos se mueven en \u00e1ngulos distintos, el visor puede interpretar otro gesto.'],
      steps: [
        { title: 'Coloca dos dedos en la pantalla', description: 'Toca la pantalla con dos dedos al mismo tiempo para iniciar el gesto de desplazamiento.' },
        { title: 'Mueve ambos dedos juntos', description: 'Desliza ambos dedos en la misma direcci\u00f3n para recolocar el modelo dentro del encuadre.' },
        { title: 'Observa el desplazamiento', description: 'El modelo deber\u00eda trasladarse con tus dedos en lugar de girar sobre su centro.' },
        { title: 'Suelta para fijar', description: 'Levanta ambos dedos cuando el modelo quede encuadrado correctamente.' },
      ],
    },
    'tap-for-info': {
      title: 'Tocar para ver informaci\u00f3n',
      shortDescription: 'Toca una parte del modelo para ver m\u00e1s informaci\u00f3n.',
      detailedDescription: 'Toca directamente una parte visible para solicitar informaci\u00f3n contextual. La interfaz deber\u00eda resaltar el objeto seleccionado y mostrar detalles de apoyo para ayudarte a entender lo que est\u00e1s viendo.',
      practicePrompt: 'Prueba a tocar un componente visible para inspeccionarlo.',
      successMessage: 'Perfecto. Has seleccionado una pieza para inspeccionarla.',
      hintLabel: 'Apunta a una parte claramente visible para obtener el mejor resultado.',
      tips: ['Gira el modelo primero si la pieza que necesitas est\u00e1 oculta.', 'Toca una vez y espera un momento a que la selecci\u00f3n se resuelva.'],
      warnings: ['Arrastrar en lugar de tocar puede girar el modelo e interrumpir la selecci\u00f3n.'],
      steps: [
        { title: 'Encuentra la pieza objetivo', description: 'Busca un componente visible que quieras inspeccionar m\u00e1s de cerca.' },
        { title: 'Toca una vez', description: 'Haz un toque limpio y \u00fanico en lugar de arrastrar o pulsar repetidamente.' },
        { title: 'Observa la respuesta visual', description: 'La pieza seleccionada deber\u00eda iluminarse o responder visualmente para confirmar la selecci\u00f3n.' },
        { title: 'Lee los detalles', description: 'Aparecer\u00e1 un panel informativo o una etiqueta contextual con m\u00e1s detalles sobre la pieza.' },
      ],
    },
    'one-finger-rotate': {
      title: 'Rotaci\u00f3n con un dedo',
      shortDescription: 'Arrastra con un dedo para girar el modelo.',
      detailedDescription: 'Usa un dedo para orbitar alrededor del modelo y revelar nuevas superficies. Es la forma principal de inspeccionar la forma, las conexiones y las zonas ocultas desde distintos \u00e1ngulos.',
      practicePrompt: 'Prueba a arrastrar con un dedo para girar el modelo.',
      successMessage: 'Bien. Has girado el modelo.',
      hintLabel: 'Los arrastres m\u00e1s largos generan cambios de \u00f3rbita mayores.',
      tips: ['Gira lentamente cuando revises detalles finos.', 'Combina rotaci\u00f3n y zoom para inspeccionar geometr\u00eda oculta.'],
      warnings: ['Usar dos dedos probablemente har\u00e1 un desplazamiento en lugar de una rotaci\u00f3n.'],
      steps: [
        { title: 'Toca con un dedo', description: 'Coloca un solo dedo sobre la pantalla para iniciar el gesto de rotaci\u00f3n.' },
        { title: 'Arrastra a izquierda o derecha', description: 'Mueve el dedo horizontalmente para orbitar alrededor del objeto y revelar lados diferentes.' },
        { title: 'Observa el giro del modelo', description: 'El modelo deber\u00eda girar sobre su centro en lugar de deslizarse por el encuadre.' },
        { title: 'Ajusta el \u00e1ngulo con precisi\u00f3n', description: 'Usa arrastres cortos para ajustes sutiles del punto de vista y una inspecci\u00f3n precisa.' },
      ],
    },
  },
  fr: {
    'pinch-to-zoom': {
      title: 'Pincer pour zoomer',
      shortDescription: 'Pincez pour zoomer sur le mod\u00e8le ou d\u00e9zoomer.',
      detailedDescription: 'Utilisez deux doigts pour modifier progressivement votre distance par rapport au mod\u00e8le. Les \u00e9carter zoome pour une inspection pr\u00e9cise, les rapprocher d\u00e9zoome pour une vue plus large.',
      practicePrompt: 'Essayez de pincer le mod\u00e8le pour zoomer ou d\u00e9zoomer.',
      successMessage: 'Bien. Vous avez chang\u00e9 le niveau de zoom.',
      hintLabel: 'D\u00e9placez les deux doigts de mani\u00e8re r\u00e9guli\u00e8re pour un zoom pr\u00e9cis.',
      tips: ['Gardez les deux doigts \u00e0 une vitesse similaire.', 'Marquez une courte pause apr\u00e8s le zoom avant d\u2019effectuer un autre geste.'],
      warnings: ['Avec un seul doigt, le mod\u00e8le pivote g\u00e9n\u00e9ralement au lieu de zoomer.'],
      steps: [
        { title: 'Posez deux doigts', description: 'Placez deux doigts sur l\u2019\u00e9cran pr\u00e8s du mod\u00e8le sans bouger l\u2019appareil.' },
        { title: '\u00c9cartez-les pour zoomer', description: '\u00c9loignez les deux doigts l\u2019un de l\u2019autre pour vous rapprocher du mod\u00e8le.' },
        { title: 'Rapprochez-les pour d\u00e9zoomer', description: 'Rapprochez les deux doigts pour reculer et voir davantage l\u2019objet.' },
        { title: 'Gardez un geste fluide', description: 'Bougez lentement et r\u00e9guli\u00e8rement pour \u00e9viter les changements brusques de distance.' },
      ],
    },
    'two-finger-drag': {
      title: 'Glisser \u00e0 deux doigts',
      shortDescription: 'Faites glisser avec deux doigts pour d\u00e9placer l\u2019objet.',
      detailedDescription: 'Utilisez deux doigts ensemble pour d\u00e9placer le mod\u00e8le dans la fen\u00eatre. C\u2019est utile pour recentrer un d\u00e9tail sans trop modifier le zoom ni la rotation.',
      practicePrompt: 'Essayez de glisser avec deux doigts pour repositionner le mod\u00e8le.',
      successMessage: 'Parfait. Vous avez recentr\u00e9 la vue.',
      hintLabel: 'Les deux doigts doivent se d\u00e9placer ensemble dans la m\u00eame direction.',
      tips: ['Utilisez de petits mouvements pour un recentrage subtil.', 'D\u00e9placez le mod\u00e8le apr\u00e8s avoir zoom\u00e9 si un d\u00e9tail est trop proche d\u2019un bord.'],
      warnings: ['Si les doigts partent dans des angles diff\u00e9rents, le visualiseur peut interpr\u00e9ter un autre geste.'],
      steps: [
        { title: 'Posez deux doigts sur l\u2019\u00e9cran', description: 'Touchez l\u2019\u00e9cran avec deux doigts en m\u00eame temps pour lancer le geste de d\u00e9placement.' },
        { title: 'D\u00e9placez les deux doigts ensemble', description: 'Faites glisser les deux doigts dans la m\u00eame direction pour replacer le mod\u00e8le dans le cadre.' },
        { title: 'Observez le d\u00e9placement', description: 'Le mod\u00e8le doit se translater avec vos doigts plut\u00f4t que pivoter autour de son centre.' },
        { title: 'Rel\u00e2chez pour terminer', description: 'Levez les deux doigts lorsque le mod\u00e8le est correctement cadr\u00e9.' },
      ],
    },
    'tap-for-info': {
      title: 'Touchez pour obtenir des infos',
      shortDescription: 'Touchez une partie du mod\u00e8le pour afficher plus d\u2019informations.',
      detailedDescription: 'Touchez directement une pi\u00e8ce visible pour demander des informations contextuelles. L\u2019interface doit mettre l\u2019objet en surbrillance et afficher des d\u00e9tails utiles pour expliquer ce que vous voyez.',
      practicePrompt: 'Essayez de toucher un composant visible pour l\u2019inspecter.',
      successMessage: 'Parfait. Vous avez s\u00e9lectionn\u00e9 une pi\u00e8ce \u00e0 inspecter.',
      hintLabel: 'Visez une pi\u00e8ce clairement visible pour obtenir le meilleur r\u00e9sultat.',
      tips: ['Faites d\u2019abord pivoter le mod\u00e8le si la pi\u00e8ce est cach\u00e9e.', 'Touchez une seule fois puis attendez un instant que la s\u00e9lection se fasse.'],
      warnings: ['Un glissement au lieu d\u2019un toucher peut faire pivoter le mod\u00e8le et interrompre la s\u00e9lection.'],
      steps: [
        { title: 'Rep\u00e9rez la pi\u00e8ce vis\u00e9e', description: 'Cherchez un composant visible que vous souhaitez inspecter de plus pr\u00e8s.' },
        { title: 'Touchez une fois', description: 'Effectuez une pression simple et nette au lieu de glisser ou de taper plusieurs fois.' },
        { title: 'Observez le retour visuel', description: 'La pi\u00e8ce s\u00e9lectionn\u00e9e doit s\u2019illuminer ou r\u00e9agir visuellement pour confirmer la s\u00e9lection.' },
        { title: 'Lisez les d\u00e9tails', description: 'Un panneau d\u2019information ou une \u00e9tiquette contextuelle s\u2019affichera avec plus de d\u00e9tails sur la pi\u00e8ce.' },
      ],
    },
    'one-finger-rotate': {
      title: 'Rotation \u00e0 un doigt',
      shortDescription: 'Faites glisser un doigt pour faire pivoter le mod\u00e8le.',
      detailedDescription: 'Utilisez un doigt pour tourner autour du mod\u00e8le et d\u00e9couvrir de nouvelles surfaces. C\u2019est la m\u00e9thode principale pour inspecter la forme, les connexions et les zones cach\u00e9es sous diff\u00e9rents angles.',
      practicePrompt: 'Essayez de faire glisser un doigt pour faire pivoter le mod\u00e8le.',
      successMessage: 'Bien. Vous avez fait pivoter le mod\u00e8le.',
      hintLabel: 'Des glissements plus longs produisent de plus grands changements d\u2019orbite.',
      tips: ['Tournez lentement pour examiner les d\u00e9tails fins.', 'Combinez rotation et zoom pour inspecter une g\u00e9om\u00e9trie cach\u00e9e.'],
      warnings: ['L\u2019utilisation de deux doigts provoquera probablement un d\u00e9placement plut\u00f4t qu\u2019une rotation.'],
      steps: [
        { title: 'Touchez avec un doigt', description: 'Posez un seul doigt sur l\u2019\u00e9cran pour lancer le geste de rotation.' },
        { title: 'Glissez \u00e0 gauche ou \u00e0 droite', description: 'D\u00e9placez le doigt horizontalement pour orbiter autour de l\u2019objet et r\u00e9v\u00e9ler d\u2019autres faces.' },
        { title: 'Observez la rotation du mod\u00e8le', description: 'Le mod\u00e8le doit tourner autour de son centre plut\u00f4t que glisser dans le cadre.' },
        { title: 'Affinez l\u2019angle', description: 'Utilisez de petits glissements pour ajuster subtilement le point de vue et inspecter avec pr\u00e9cision.' },
      ],
    },
  },
  de: {
    'pinch-to-zoom': {
      title: 'Zum Zoomen zusammenziehen',
      shortDescription: 'Ziehen Sie zwei Finger zusammen oder auseinander, um das Modell zu zoomen.',
      detailedDescription: 'Verwenden Sie zwei Finger, um Ihren Abstand zum Modell sanft zu ver\u00e4ndern. Wenn Sie die Finger auseinanderziehen, zoomen Sie f\u00fcr eine genauere Ansicht hinein. Wenn Sie sie zusammenziehen, zoomen Sie wieder heraus.',
      practicePrompt: 'Versuchen Sie, das Modell mit zwei Fingern hinein- oder herauszuzoomen.',
      successMessage: 'Gut. Sie haben die Zoomstufe ge\u00e4ndert.',
      hintLabel: 'Bewegen Sie beide Finger gleichm\u00e4\u00dfig f\u00fcr eine pr\u00e4zise Zoom-Steuerung.',
      tips: ['Bewegen Sie beide Finger mit \u00e4hnlicher Geschwindigkeit.', 'Machen Sie nach dem Zoomen kurz Pause, bevor Sie eine weitere Geste beginnen.'],
      warnings: ['Mit nur einem Finger wird das Modell meist gedreht statt gezoomt.'],
      steps: [
        { title: 'Zwei Finger auflegen', description: 'Legen Sie zwei Finger nahe am Modell auf den Bildschirm, ohne das Ger\u00e4t zu bewegen.' },
        { title: 'Auseinanderziehen zum Hineinzoomen', description: 'Bewegen Sie beide Finger voneinander weg, um n\u00e4her an das Modell heranzukommen.' },
        { title: 'Zusammenziehen zum Herauszoomen', description: 'F\u00fchren Sie beide Finger zusammen, um wieder mehr vom Objekt zu sehen.' },
        { title: 'Gleichm\u00e4\u00dfig bewegen', description: 'Bewegen Sie sich langsam und ruhig, damit die Zoomdistanz nicht sprunghaft \u00e4ndert.' },
      ],
    },
    'two-finger-drag': {
      title: 'Zwei-Finger-Ziehen',
      shortDescription: 'Ziehen Sie mit zwei Fingern, um das Objekt zu verschieben.',
      detailedDescription: 'Verwenden Sie zwei Finger gleichzeitig, um das Modell im Sichtfeld zu verschieben. So k\u00f6nnen Sie ein Detail neu zentrieren, ohne Zoom oder Rotation stark zu ver\u00e4ndern.',
      practicePrompt: 'Versuchen Sie, das Modell mit zwei Fingern neu zu positionieren.',
      successMessage: 'Gut. Sie haben die Ansicht neu positioniert.',
      hintLabel: 'Beide Finger sollten sich gemeinsam in dieselbe Richtung bewegen.',
      tips: ['Verwenden Sie kurze Bewegungen f\u00fcr ein feines Neuzentrieren.', 'Verschieben Sie nach dem Zoomen, wenn ein Detail zu nah am Rand liegt.'],
      warnings: ['Wenn sich die Finger in verschiedene Winkel bewegen, kann der Viewer eine andere Geste erkennen.'],
      steps: [
        { title: 'Zwei Finger auf den Bildschirm legen', description: 'Ber\u00fchren Sie den Bildschirm mit zwei Fingern gleichzeitig, um die Verschiebegeste zu starten.' },
        { title: 'Beide Finger gemeinsam bewegen', description: 'Schieben Sie beide Finger in dieselbe Richtung, um das Modell im Bildausschnitt zu versetzen.' },
        { title: 'Verschiebung beobachten', description: 'Das Modell sollte sich mit Ihren Fingern bewegen und nicht um sein Zentrum rotieren.' },
        { title: 'Zum Beenden loslassen', description: 'Heben Sie beide Finger an, sobald das Modell richtig ausgerichtet ist.' },
      ],
    },
    'tap-for-info': {
      title: 'Tippen f\u00fcr Informationen',
      shortDescription: 'Tippen Sie auf einen Teil des Modells, um mehr Informationen zu sehen.',
      detailedDescription: 'Tippen Sie direkt auf ein sichtbares Teil, um kontextbezogene Informationen anzufordern. Die Oberfl\u00e4che sollte das ausgew\u00e4hlte Objekt hervorheben und zus\u00e4tzliche Details anzeigen.',
      practicePrompt: 'Versuchen Sie, auf ein sichtbares Bauteil zu tippen, um es zu inspizieren.',
      successMessage: 'Perfekt. Sie haben ein Teil zur Inspektion ausgew\u00e4hlt.',
      hintLabel: 'Zielen Sie auf ein klar sichtbares Teil, um das beste Ergebnis zu erhalten.',
      tips: ['Drehen Sie das Modell zuerst, wenn das ben\u00f6tigte Teil verdeckt ist.', 'Tippen Sie einmal und warten Sie kurz, bis die Auswahl verarbeitet wurde.'],
      warnings: ['Wenn Sie ziehen statt tippen, kann sich das Modell drehen und die Auswahl unterbrechen.'],
      steps: [
        { title: 'Zielteil finden', description: 'Suchen Sie ein sichtbares Bauteil, das Sie genauer betrachten m\u00f6chten.' },
        { title: 'Einmal tippen', description: 'Tippen Sie einmal sauber, statt zu ziehen oder wiederholt zu dr\u00fccken.' },
        { title: 'Visuelle R\u00fcckmeldung beobachten', description: 'Das gew\u00e4hlte Teil sollte leuchten oder visuell reagieren, um die Auswahl zu best\u00e4tigen.' },
        { title: 'Details lesen', description: 'Ein Informationsfeld oder kontextbezogenes Label erscheint mit weiteren Angaben zum Teil.' },
      ],
    },
    'one-finger-rotate': {
      title: 'Ein-Finger-Drehung',
      shortDescription: 'Ziehen Sie mit einem Finger, um das Modell zu drehen.',
      detailedDescription: 'Verwenden Sie einen Finger, um um das Modell herum zu kreisen und neue Fl\u00e4chen sichtbar zu machen. Das ist die wichtigste Methode, um Form, Verbindungen und verborgene Bereiche aus verschiedenen Blickwinkeln zu pr\u00fcfen.',
      practicePrompt: 'Versuchen Sie, das Modell mit einem Finger zu drehen.',
      successMessage: 'Gut. Sie haben das Modell gedreht.',
      hintLabel: 'L\u00e4ngere Bewegungen f\u00fchren zu gr\u00f6\u00dferen Orbit-\u00c4nderungen.',
      tips: ['Drehen Sie langsam, wenn Sie feine Details pr\u00fcfen.', 'Kombinieren Sie Rotation und Zoom, um verborgene Geometrie zu inspizieren.'],
      warnings: ['Mit zwei Fingern wird das Modell wahrscheinlich verschoben statt gedreht.'],
      steps: [
        { title: 'Mit einem Finger ber\u00fchren', description: 'Legen Sie einen Finger auf den Bildschirm, um die Drehgeste zu starten.' },
        { title: 'Nach links oder rechts ziehen', description: 'Bewegen Sie den Finger horizontal, um das Objekt zu umrunden und andere Seiten sichtbar zu machen.' },
        { title: 'Drehung des Modells beobachten', description: 'Das Modell sollte sich um sein Zentrum drehen und nicht im Bild verschoben werden.' },
        { title: 'Winkel fein abstimmen', description: 'Nutzen Sie kurze Bewegungen f\u00fcr feine Blickwinkelanpassungen und eine pr\u00e4zise Inspektion.' },
      ],
    },
  },
  zh: {
    'pinch-to-zoom': {
      title: '\u53cc\u6307\u7f29\u653e',
      shortDescription: '\u53cc\u6307\u634f\u5408\u53ef\u5bf9\u6a21\u578b\u653e\u5927\u6216\u7f29\u5c0f\u3002',
      detailedDescription: '\u4f7f\u7528\u4e24\u6839\u624b\u6307\u5e73\u7a33\u5730\u6539\u53d8\u4f60\u4e0e\u6a21\u578b\u7684\u8ddd\u79bb\u3002\u5411\u5916\u5206\u5f00\u624b\u6307\u53ef\u653e\u5927\u4ee5\u4fbf\u8fd1\u8ddd\u79bb\u67e5\u770b\uff0c\u5411\u5185\u5408\u62e2\u5219\u53ef\u7f29\u5c0f\u4ee5\u83b7\u5f97\u66f4\u5927\u89c6\u91ce\u3002',
      practicePrompt: '\u5c1d\u8bd5\u53cc\u6307\u634f\u5408\u6a21\u578b\u8fdb\u884c\u653e\u5927\u6216\u7f29\u5c0f\u3002',
      successMessage: '\u505a\u5f97\u597d\u3002\u4f60\u5df2\u7ecf\u6539\u53d8\u4e86\u7f29\u653e\u7ea7\u522b\u3002',
      hintLabel: '\u4e24\u6839\u624b\u6307\u5c3d\u91cf\u4ee5\u76f8\u540c\u901f\u5ea6\u79fb\u52a8\uff0c\u4ee5\u83b7\u5f97\u66f4\u7cbe\u786e\u7684\u7f29\u653e\u63a7\u5236\u3002',
      tips: ['\u4fdd\u6301\u4e24\u6839\u624b\u6307\u4ee5\u76f8\u4f3c\u901f\u5ea6\u79fb\u52a8\u3002', '\u7f29\u653e\u540e\u7a0d\u4f5c\u505c\u987f\uff0c\u518d\u5f00\u59cb\u4e0b\u4e00\u4e2a\u624b\u52bf\u3002'],
      warnings: ['\u53ea\u7528\u4e00\u6839\u624b\u6307\u65f6\uff0c\u901a\u5e38\u4f1a\u65cb\u8f6c\u6a21\u578b\uff0c\u800c\u4e0d\u662f\u7f29\u653e\u3002'],
      steps: [
        { title: '\u653e\u4e0b\u4e24\u6839\u624b\u6307', description: '\u5728\u6a21\u578b\u9644\u8fd1\u5c06\u4e24\u6839\u624b\u6307\u653e\u5728\u5c4f\u5e55\u4e0a\uff0c\u4f46\u4e0d\u8981\u79fb\u52a8\u8bbe\u5907\u3002' },
        { title: '\u5411\u5916\u5206\u5f00\u4ee5\u653e\u5927', description: '\u5c06\u4e24\u6839\u624b\u6307\u5411\u76f8\u53cd\u65b9\u5411\u79fb\u5f00\uff0c\u4ee5\u9760\u8fd1\u6a21\u578b\u3002' },
        { title: '\u5411\u5185\u5408\u62e2\u4ee5\u7f29\u5c0f', description: '\u5c06\u4e24\u6839\u624b\u6307\u76f8\u4e92\u9760\u8fd1\uff0c\u4ee5\u9000\u540e\u67e5\u770b\u66f4\u591a\u5185\u5bb9\u3002' },
        { title: '\u4fdd\u6301\u5e73\u7a33\u52a8\u4f5c', description: '\u6162\u901f\u4e14\u5747\u5300\u5730\u79fb\u52a8\uff0c\u907f\u514d\u7f29\u653e\u8ddd\u79bb\u51fa\u73b0\u7a81\u53d8\u3002' },
      ],
    },
    'two-finger-drag': {
      title: '\u53cc\u6307\u62d6\u52a8',
      shortDescription: '\u7528\u4e24\u6839\u624b\u6307\u62d6\u52a8\u53ef\u79fb\u52a8\u5bf9\u8c61\u3002',
      detailedDescription: '\u540c\u65f6\u4f7f\u7528\u4e24\u6839\u624b\u6307\u53ef\u5728\u89c6\u53e3\u5185\u79fb\u52a8\u6a21\u578b\u3002\u8fd9\u5728\u4e0d\u5927\u5e45\u6539\u53d8\u7f29\u653e\u6216\u65cb\u8f6c\u7684\u60c5\u51b5\u4e0b\uff0c\u5f88\u9002\u5408\u91cd\u65b0\u5c45\u4e2d\u67d0\u4e2a\u7ec6\u8282\u3002',
      practicePrompt: '\u8bd5\u7740\u7528\u53cc\u6307\u62d6\u52a8\u6765\u91cd\u65b0\u5b9a\u4f4d\u6a21\u578b\u3002',
      successMessage: '\u5f88\u597d\u3002\u4f60\u5df2\u91cd\u65b0\u5b9a\u4f4d\u89c6\u56fe\u3002',
      hintLabel: '\u4e24\u6839\u624b\u6307\u5e94\u8be5\u4e00\u8d77\u5411\u540c\u4e00\u65b9\u5411\u79fb\u52a8\u3002',
      tips: ['\u4f7f\u7528\u77ed\u8ddd\u79bb\u79fb\u52a8\u53ef\u8fdb\u884c\u7ec6\u5fae\u5c45\u4e2d\u3002', '\u82e5\u7ec6\u8282\u592a\u63a5\u8fd1\u5c4f\u5e55\u8fb9\u7f18\uff0c\u53ef\u5728\u7f29\u653e\u540e\u518d\u5e73\u79fb\u3002'],
      warnings: ['\u5982\u679c\u4e24\u6839\u624b\u6307\u79fb\u52a8\u89d2\u5ea6\u4e0d\u4e00\u81f4\uff0c\u67e5\u770b\u5668\u53ef\u80fd\u4f1a\u8bc6\u522b\u4e3a\u5176\u4ed6\u624b\u52bf\u3002'],
      steps: [
        { title: '\u5c06\u4e24\u6839\u624b\u6307\u653e\u5728\u5c4f\u5e55\u4e0a', description: '\u540c\u65f6\u7528\u4e24\u6839\u624b\u6307\u89e6\u6478\u5c4f\u5e55\uff0c\u5f00\u59cb\u5e73\u79fb\u624b\u52bf\u3002' },
        { title: '\u4e24\u6307\u4e00\u8d77\u79fb\u52a8', description: '\u5c06\u4e24\u6839\u624b\u6307\u5411\u540c\u4e00\u65b9\u5411\u6ed1\u52a8\uff0c\u4ee5\u91cd\u65b0\u8c03\u6574\u6a21\u578b\u5728\u753b\u9762\u4e2d\u7684\u4f4d\u7f6e\u3002' },
        { title: '\u89c2\u5bdf\u89c6\u56fe\u4f4d\u79fb', description: '\u6a21\u578b\u5e94\u968f\u624b\u6307\u5e73\u79fb\uff0c\u800c\u4e0d\u662f\u56f4\u7ed5\u4e2d\u5fc3\u65cb\u8f6c\u3002' },
        { title: '\u653e\u5f00\u7ed3\u675f', description: '\u5f53\u6a21\u578b\u4f4d\u7f6e\u5408\u9002\u65f6\uff0c\u62ac\u8d77\u4e24\u6839\u624b\u6307\u7ed3\u675f\u64cd\u4f5c\u3002' },
      ],
    },
    'tap-for-info': {
      title: '\u70b9\u51fb\u67e5\u770b\u4fe1\u606f',
      shortDescription: '\u70b9\u51fb\u6a21\u578b\u7684\u67d0\u4e2a\u90e8\u5206\u53ef\u67e5\u770b\u66f4\u591a\u4fe1\u606f\u3002',
      detailedDescription: '\u76f4\u63a5\u70b9\u51fb\u53ef\u89c1\u7684\u90e8\u4ef6\u4ee5\u83b7\u53d6\u4e0a\u4e0b\u6587\u4fe1\u606f\u3002\u754c\u9762\u5e94\u4f1a\u9ad8\u4eae\u9009\u4e2d\u5bf9\u8c61\uff0c\u5e76\u663e\u793a\u76f8\u5173\u8bf4\u660e\uff0c\u5e2e\u52a9\u4f60\u7406\u89e3\u5f53\u524d\u67e5\u770b\u7684\u5185\u5bb9\u3002',
      practicePrompt: '\u8bd5\u7740\u70b9\u51fb\u4e00\u4e2a\u53ef\u89c1\u90e8\u4ef6\u8fdb\u884c\u67e5\u770b\u3002',
      successMessage: '\u5f88\u68d2\u3002\u4f60\u5df2\u9009\u4e2d\u4e00\u4e2a\u90e8\u4ef6\u8fdb\u884c\u67e5\u770b\u3002',
      hintLabel: '\u5c3d\u91cf\u70b9\u51fb\u6e05\u6670\u53ef\u89c1\u7684\u90e8\u4ef6\uff0c\u4ee5\u83b7\u5f97\u6700\u4f73\u6548\u679c\u3002',
      tips: ['\u5982\u679c\u76ee\u6807\u90e8\u4ef6\u88ab\u906e\u6321\uff0c\u53ef\u4ee5\u5148\u65cb\u8f6c\u6a21\u578b\u3002', '\u70b9\u51fb\u4e00\u6b21\u540e\u7a0d\u7b49\u7247\u523b\uff0c\u8ba9\u9009\u62e9\u5b8c\u6210\u3002'],
      warnings: ['\u5982\u679c\u4f7f\u7528\u62d6\u52a8\u800c\u4e0d\u662f\u70b9\u51fb\uff0c\u53ef\u80fd\u4f1a\u65cb\u8f6c\u6a21\u578b\u5e76\u4e2d\u65ad\u9009\u62e9\u3002'],
      steps: [
        { title: '\u627e\u5230\u76ee\u6807\u90e8\u4ef6', description: '\u5bfb\u627e\u4f60\u60f3\u8981\u8fdb\u4e00\u6b65\u67e5\u770b\u7684\u53ef\u89c1\u7ec4\u4ef6\u3002' },
        { title: '\u70b9\u51fb\u4e00\u6b21', description: '\u8fdb\u884c\u4e00\u6b21\u5e72\u51c0\u5229\u843d\u7684\u70b9\u51fb\uff0c\u4e0d\u8981\u62d6\u52a8\u6216\u53cd\u590d\u6309\u538b\u3002' },
        { title: '\u89c2\u5bdf\u9ad8\u4eae\u53cd\u9988', description: '\u9009\u4e2d\u7684\u90e8\u4ef6\u5e94\u8be5\u4f1a\u53d1\u5149\u6216\u51fa\u73b0\u53ef\u89c6\u53cd\u5e94\uff0c\u4ee5\u786e\u8ba4\u9009\u62e9\u5df2\u6210\u529f\u3002' },
        { title: '\u9605\u8bfb\u8be6\u60c5', description: '\u5c06\u51fa\u73b0\u4fe1\u606f\u9762\u677f\u6216\u4e0a\u4e0b\u6587\u6807\u7b7e\uff0c\u663e\u793a\u66f4\u591a\u90e8\u4ef6\u8be6\u60c5\u3002' },
      ],
    },
    'one-finger-rotate': {
      title: '\u5355\u6307\u65cb\u8f6c',
      shortDescription: '\u7528\u4e00\u6839\u624b\u6307\u62d6\u52a8\u53ef\u65cb\u8f6c\u6a21\u578b\u3002',
      detailedDescription: '\u4f7f\u7528\u4e00\u6839\u624b\u6307\u56f4\u7ed5\u6a21\u578b\u8fdb\u884c\u73af\u7ed5\uff0c\u4ece\u800c\u67e5\u770b\u65b0\u7684\u8868\u9762\u3002\u8fd9\u662f\u4ece\u4e0d\u540c\u89d2\u5ea6\u68c0\u67e5\u5f62\u72b6\u3001\u8fde\u63a5\u548c\u9690\u85cf\u533a\u57df\u7684\u4e3b\u8981\u65b9\u5f0f\u3002',
      practicePrompt: '\u8bd5\u7740\u7528\u4e00\u6839\u624b\u6307\u62d6\u52a8\u6765\u65cb\u8f6c\u6a21\u578b\u3002',
      successMessage: '\u505a\u5f97\u597d\u3002\u4f60\u5df2\u7ecf\u65cb\u8f6c\u4e86\u6a21\u578b\u3002',
      hintLabel: '\u62d6\u52a8\u8ddd\u79bb\u8d8a\u957f\uff0c\u8f68\u9053\u53d8\u5316\u8d8a\u5927\u3002',
      tips: ['\u68c0\u67e5\u7ec6\u8282\u65f6\u8bf7\u6162\u901f\u65cb\u8f6c\u3002', '\u53ef\u5c06\u65cb\u8f6c\u4e0e\u7f29\u653e\u7ed3\u5408\uff0c\u4ee5\u68c0\u67e5\u9690\u85cf\u7ed3\u6784\u3002'],
      warnings: ['\u4f7f\u7528\u4e24\u6839\u624b\u6307\u66f4\u53ef\u80fd\u662f\u5e73\u79fb\uff0c\u800c\u4e0d\u662f\u65cb\u8f6c\u3002'],
      steps: [
        { title: '\u7528\u4e00\u6839\u624b\u6307\u89e6\u6478', description: '\u5728\u5c4f\u5e55\u4e0a\u653e\u4e0b\u4e00\u6839\u624b\u6307\uff0c\u5f00\u59cb\u65cb\u8f6c\u624b\u52bf\u3002' },
        { title: '\u5411\u5de6\u6216\u5411\u53f3\u62d6\u52a8', description: '\u6c34\u5e73\u79fb\u52a8\u624b\u6307\uff0c\u56f4\u7ed5\u5bf9\u8c61\u65cb\u8f6c\uff0c\u67e5\u770b\u4e0d\u540c\u4fa7\u9762\u3002' },
        { title: '\u89c2\u5bdf\u6a21\u578b\u65cb\u8f6c', description: '\u6a21\u578b\u5e94\u56f4\u7ed5\u4e2d\u5fc3\u65cb\u8f6c\uff0c\u800c\u4e0d\u662f\u5728\u753b\u9762\u4e2d\u6ed1\u52a8\u3002' },
        { title: '\u5fae\u8c03\u89d2\u5ea6', description: '\u4f7f\u7528\u8f83\u77ed\u7684\u62d6\u52a8\uff0c\u53ef\u8fdb\u884c\u66f4\u7cbe\u7ec6\u7684\u89c6\u89d2\u8c03\u6574\u548c\u7cbe\u51c6\u68c0\u67e5\u3002' },
      ],
    },
  },
};

function localizeInteraction(interaction: TutorialInteractionDetail, language: LanguageCode): TutorialInteractionDetail {
  const localized = TUTORIAL_TRANSLATIONS[language]?.[interaction.id];
  if (!localized) {
    return interaction;
  }

  return {
    ...interaction,
    title: localized.title,
    shortDescription: localized.shortDescription,
    detailedDescription: localized.detailedDescription,
    practicePrompt: localized.practicePrompt,
    successMessage: localized.successMessage,
    hintLabel: localized.hintLabel,
    tips: localized.tips,
    warnings: localized.warnings,
    steps: interaction.steps.map((step, index) => ({
      ...step,
      title: localized.steps[index]?.title ?? step.title,
      description: localized.steps[index]?.description ?? step.description,
    })),
  };
}

export function getTutorialInteractionById(
  id: string | null | undefined,
  language: LanguageCode = 'en'
): TutorialInteractionDetail | null {
  if (!id) return null;
  const interaction = BASE_TUTORIAL_INTERACTIONS.find((candidate) => candidate.id === id) ?? null;
  return interaction ? localizeInteraction(interaction, language) : null;
}

export function getTutorialSummaryItems(language: LanguageCode = 'en') {
  return BASE_TUTORIAL_INTERACTIONS.map((interaction) => {
    const localized = localizeInteraction(interaction, language);
    return {
      id: localized.id,
      icon: localized.icon,
      title: localized.title,
      description: localized.shortDescription,
    };
  });
}
